import { IF, nil, NO, UIColor, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHFileUploadView } from "../../Custom components/RH/RHFileUploadView"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { formatRHDateTime } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHButtons, RHColors, RHFont, RHRadius, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHCompanyQuestionsCardViewTokens,
    RHPrivateMessageThreadViewTokens,
    RHQuestionAnswerPairViewTokens
} from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHProcurementPrivateMessage, RHProcurementPrivateMessageSenderRole } from "../../SocketClientFunctions"

const PRIVATE_MESSAGE_INPUT_ID = "private-message-thread-input"


// ─────────────────────────────────────────────────────────────────────────────
// Private messages reuse the public information exchange banner component;
// only the surrounding conversation alignment is specific to this view.
// ─────────────────────────────────────────────────────────────────────────────

class RHPrivateMessageBubbleView extends UIView {

    readonly senderLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: "600",
                fontFamily: RHFont.familyBase,
                lineHeight: "1.2"
            }
        })
        .addedAsSubviewToView(this)

    readonly messageBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this)

    readonly timestampLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: "400",
                fontFamily: RHFont.familyBase,
                lineHeight: "1.2"
            }
        })
        .addedAsSubviewToView(this)

    readonly filesListView = new RHFileListView()
        .configuredWithObject({
            variant: RHFileListView.variant.compactDownload
        })

    /** Whether this bubble belongs to the currently logged-in viewer's role (right-hand side). */
    _isSelf = NO

    configure(message: RHProcurementPrivateMessage, isSelf: boolean, sourceLabel?: string) {
        this._isSelf = isSelf

        const fallbackSenderName = IF(message.senderRole === "procurer")(() =>
            "Tundmatu hankija"
        ).ELSE(() =>
            "Tundmatu pakkuja"
        )
        this.senderLabel.text = message.senderCompanyName || fallbackSenderName
        if (sourceLabel) {
            this.senderLabel.text += ` · ${sourceLabel}`
        }
        this.messageBanner.configureBannerWithObject({
            text: message.bodyText,
            fontSize: RHFont.sizeSmall,
            fontWeight: RHQuestionAnswerPairViewTokens.messageTextFontWeight,
            fontFamily: RHFont.familyBase
        })
        if (message.senderRole === "procurer") {
            this.messageBanner.alertType = RHAlertBannerView.type.ProcurerBroadcast
        }
        else {
            this.messageBanner.configureBannerWithObject({
                backgroundColor: RHColors.surfaceSubtle,
                borderColor: RHColors.slateMedium,
                textColor: RHColors.textPrimary
            })
            this.messageBanner.setBorder(
                nil,
                RHBadges.alertBannerGradientBorderWidth,
                RHColors.slateMedium
            )
        }

        if (message.bodyFileIds.length > 0) {
            this.filesListView.addedAsSubviewToView(this)
            this.filesListView.downloadEnabled = YES
            this.filesListView.deleteEnabled = NO
            this.filesListView.loadFilesWithIds(message.bodyFileIds)
        }
        else {
            this.filesListView.removeFromSuperview()
        }

        const date = new Date(message.createdAt)
        this.timestampLabel.text = formatRHDateTime(date)

        this.senderLabel.configureWithObject({
            textColor: RHColors.textPrimary,
            style: { textAlign: "left" }
        })
        this.timestampLabel.configureWithObject({
            textColor: RHColors.textTimestamp,
            style: { textAlign: "right" }
        })
    }

    preferredContentWidth(maxWidth: number) {
        const metadataWidth =
            this.senderLabel.intrinsicContentWidth(RHQuestionAnswerPairViewTokens.metadataRowHeight)
            + this.timestampLabel.intrinsicContentWidth(RHQuestionAnswerPairViewTokens.metadataRowHeight)
            + RHSpacing.small
        const textWidth = this.messageBanner._textLabel.intrinsicContentWidth(0) + RHSpacing.large * 2
        return [
            maxWidth,
            [
                RHPrivateMessageThreadViewTokens.messageMinimumWidth,
                metadataWidth,
                textWidth
            ].max()
        ].min()
    }

    override intrinsicContentHeight(constrainingWidth: number = 0): number {
        let height = RHQuestionAnswerPairViewTokens.metadataRowHeight
            + RHSpacing.small
            + this.messageBanner.intrinsicContentHeight(constrainingWidth)

        if (this.filesListView.superview === this) {
            height += RHSpacing.small + this.filesListView.intrinsicContentHeight(constrainingWidth)
        }

        return height
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const metadataRowHeight = RHQuestionAnswerPairViewTokens.metadataRowHeight
        const bounds = this.contentBoundsWithInset(0)
        const timestampWidth = [
            bounds.width,
            this.timestampLabel.intrinsicContentWidth(metadataRowHeight) + RHSpacing.small
        ].min()
        const senderWidth = [
            0,
            bounds.width - timestampWidth - RHSpacing.small
        ].max()

        const metadataRowFrame = bounds.rectangleWithHeight(metadataRowHeight)

        metadataRowFrame
            .distributeViewsAlongWidth(
                [this.senderLabel, this.timestampLabel],
                undefined,
                RHSpacing.small,
                [senderWidth, timestampWidth]
            )

        const messageFrame = metadataRowFrame
            .rectangleForNextRow(RHSpacing.small, this.messageBanner)
            .assignedAsFrameOfView(this.messageBanner)

        if (this.filesListView.superview === this) {
            messageFrame
                .rectangleForNextRow(RHSpacing.small, this.filesListView)
                .assignedAsFrameOfView(this.filesListView)
        }
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// Scroll container that holds all bubbles
// ─────────────────────────────────────────────────────────────────────────────

class RHPrivateMessageListView extends UINativeScrollView {

    _bubbles: RHPrivateMessageBubbleView[] = []

    constructor() {
        super()
        ;(this as RHPrivateMessageListView).configureWithObject({
            style: {
                overflowX: "hidden",
                overflowY: "auto"
            }
        })
    }

    replaceMessages(
        messages: RHProcurementPrivateMessage[],
        currentViewerRole: RHProcurementPrivateMessageSenderRole,
        sourceLabelsByProcurementId: Map<string, string>
    ) {
        this._bubbles.everyElement.removeFromSuperview()
        this._bubbles = []

        for (const message of messages) {
            const bubble = new RHPrivateMessageBubbleView()
            let sourceLabel: string | undefined
            if (sourceLabelsByProcurementId.size > 1) {
                sourceLabel = sourceLabelsByProcurementId.get(message.procurement)
            }
            bubble.configure(message, message.senderRole === currentViewerRole, sourceLabel)
            bubble.addedAsSubviewToView(this)
            this._bubbles.push(bubble)
        }

        this.setNeedsLayout()
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(RHSpacing.small)

        if (this._bubbles.length === 0) {
            return
        }

        const maxBubbleWidth = bounds.width * RHPrivateMessageThreadViewTokens.messageMaximumWidthRatio

        let current = bounds.rectangleWithHeight(0)

        for (const bubble of this._bubbles) {
            const bubbleWidth = bubble.preferredContentWidth(maxBubbleWidth)
            const bubbleHeight = bubble.intrinsicContentHeight(bubbleWidth)
            const rowFrame = current.rectangleForNextRow(RHSpacing.small, bubbleHeight)

            if (bubble._isSelf) {
                bubble.frame = rowFrame
                    .rectangleWithInsets(0, RHSpacing.xxLarge, 0, 0)
                    .rectangleWithWidth(bubbleWidth, 1)
            }
            else {
                bubble.frame = rowFrame
                    .rectangleWithWidth(bubbleWidth, 0)
            }

            current = rowFrame
        }
    }

    override intrinsicContentHeight(constrainingWidth?: number): number {
        // This view fills a fixed height assigned by the parent — use 0 so
        // the parent's fixed height assignment drives the layout.
        return 0
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// Main thread view — the currently selected private thread, either in the
// procurer's split view or standalone in the offerer's single-thread view.
// ─────────────────────────────────────────────────────────────────────────────

export class RHPrivateMessageThreadView extends UIView {

    sourceLabelsByProcurementId = new Map<string, string>()

    readonly headerView = new UIView()
        .configuredWithObject({
            style: { borderBottom: `1px solid ${RHColors.dividerAlpha70.stringValue}` }
        })
        .addedAsSubviewToView(this)

    readonly conversationTitleLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            textColor: RHColors.textPrimary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.companyNameFontWeight,
                fontFamily: RHFont.familyBase,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }
        })
        .addedAsSubviewToView(this.headerView)

    readonly lastActivityLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            textColor: RHColors.textTimestamp,
            textAlignment: UITextView.textAlignment.right,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.lastUpdateFontWeight,
                fontFamily: RHFont.familyBase,
                whiteSpace: "nowrap"
            }
        })
        .addedAsSubviewToView(this.headerView)

    readonly emptyStateLabel = new UITextView()
        .configuredWithObject({
            text: "Vestlust ei ole veel alustatud.",
            textColor: RHColors.textTimestamp,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                textAlign: "center",
                padding: `${RHSpacing.medium}px`
            }
        })

    readonly messageListView = new RHPrivateMessageListView()
        .addedAsSubviewToView(this)

    readonly dividerView = new UIView()
        .configuredWithObject({
            style: { borderTop: `1px solid ${RHColors.dividerAlpha70.stringValue}` }
        })
        .addedAsSubviewToView(this)

    readonly fileUploadView = new RHFileUploadView()
        .configuredWithObject({
            variant: RHFileUploadView.variant.compact,
            bulkDownloadEnabled: NO
        })
        .addedAsSubviewToView(this)

    readonly inputArea = new RHTextArea(PRIVATE_MESSAGE_INPUT_ID)
        .configuredWithObject({
            placeholderText: "Kirjuta sõnum…"
        })
        .addedAsSubviewToView(this)

    readonly sendButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Saada" },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    void this._sendMessage()
                }
            }
        })
        .addedAsSubviewToView(this)

    _procurementId?: string
    _offererCompanyId?: string
    _currentViewerRole: RHProcurementPrivateMessageSenderRole = "procurer"
    _hasMessages = NO
    _canSend = NO
    _isSending = NO

    constructor(elementID?: string) {
        super(elementID)
        ;(this as RHPrivateMessageThreadView).configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: `1px solid ${RHColors.borderSlate.stringValue}`,
                borderRadius: `${RHRadius.large}px`,
                overflow: "hidden"
            }
        })

        UIView.injectCSS(`
            #${PRIVATE_MESSAGE_INPUT_ID}::placeholder,
            #${PRIVATE_MESSAGE_INPUT_ID}_textElement::placeholder {
                color: ${RHColors.textPlaceholder.stringValue} !important;
                -webkit-text-fill-color: ${RHColors.textPlaceholder.stringValue} !important;
                opacity: 1;
            }
        `, "rh-private-message-thread-input-placeholder")

        this.inputArea.controlEventTargetAccumulator.EnterDown = (_sender: UIView, event: Event) => {
            if (!(event instanceof KeyboardEvent) || event.shiftKey) {
                return
            }
            event.preventDefault()
            void this._sendMessage()
        }
        this.inputArea.controlEventTargetAccumulator.TextChange = () => {
            this.setNeedsLayoutUpToRootView()
        }
        this.fileUploadView.dropZone.label.configureWithObject({
            isSingleLine: YES,
            style: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }
        })
    }

    configureWithThread(input: {
        procurementId: string
        offererCompanyId: string
        conversationTitle: string
        messages: RHProcurementPrivateMessage[]
        currentViewerRole: RHProcurementPrivateMessageSenderRole
        canSend: boolean
    }) {
        const conversationDidChange = this._procurementId !== input.procurementId ||
            this._offererCompanyId !== input.offererCompanyId
        this._procurementId = input.procurementId
        this._offererCompanyId = input.offererCompanyId
        this._currentViewerRole = input.currentViewerRole
        this._hasMessages = input.messages.length > 0
        this._canSend = input.canSend
        this.conversationTitleLabel.text = input.conversationTitle
        const lastMessage = input.messages[input.messages.length - 1]
        this.lastActivityLabel.text = IF(!!lastMessage)(() =>
            `Viimane aktiivsus ${formatRHDateTime(new Date(lastMessage.createdAt))}`
        ).ELSE(() =>
            ""
        )

        this.messageListView.replaceMessages(input.messages, input.currentViewerRole, this.sourceLabelsByProcurementId)
        if (conversationDidChange) {
            this.inputArea.text = ""
            this.fileUploadView.clearFiles()
        }
        this._updateInputVisibility()
        this.setNeedsLayoutUpToRootView()

        setTimeout(() => {
            this.messageListView.scrollToBottom()
        }, 50)
    }

    _updateInputVisibility() {
        if (this._canSend || this._hasMessages) {
            this.emptyStateLabel.removeFromSuperview()
        }
        else if (this.emptyStateLabel.superview !== this) {
            this.emptyStateLabel.addedAsSubviewToView(this)
        }

        this.inputArea.enabled = this._canSend && !this._isSending
        this.fileUploadView.enabled = this._canSend && !this._isSending
        this.sendButton.enabled = this._canSend && !this._isSending
        this.setNeedsLayoutUpToRootView()
    }

    async _sendMessage() {
        if (!this._procurementId || !this._offererCompanyId || !this._canSend || this._isSending) {
            return
        }

        const text = this.inputArea.text.trim()
        if (!text) {
            return
        }

        this._isSending = YES
        this._updateInputVisibility()

        try {
            await RHCore.sharedInstance.privateMessageStore.sendMessage({
                procurementId: this._procurementId,
                offererCompanyId: this._offererCompanyId,
                bodyText: text,
                fileIds: this.fileUploadView.uploadedFiles.map(uploadedFile => uploadedFile.id)
            })
            this.inputArea.text = ""
            this.fileUploadView.clearFiles()
        }
        catch (error) {
            console.error("[RHPrivateMessageThreadView] Failed to send message:", error)
        }

        this._isSending = NO
        this._updateInputVisibility()
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        const headerHeight = RHPrivateMessageThreadViewTokens.conversationHeaderHeight
        const sendButtonWidth = RHPrivateMessageThreadViewTokens.composerSendButtonWidth
        const composerContentWidth = Math.max(0, bounds.width - RHSpacing.small * 2)
        const uploadWidth = composerContentWidth
        const inputWidth = Math.max(
            0,
            composerContentWidth - sendButtonWidth - RHSpacing.small
        )
        bounds
            .rectangleWithWidth(inputWidth)
            .rectangleWithHeight(RHPrivateMessageThreadViewTokens.composerMinimumHeight)
            .assignedAsFrameOfView(this.inputArea)
        const inputContentHeight = this.inputArea.textElementView.viewHTMLElement.scrollHeight
        const inputHeight = Math.min(
            RHPrivateMessageThreadViewTokens.composerMaximumHeight,
            Math.max(
                RHPrivateMessageThreadViewTokens.composerMinimumHeight,
                this.inputArea.intrinsicContentHeight(inputWidth),
                inputContentHeight
            )
        )
        const uploadHeight = this.fileUploadView.intrinsicContentHeight(uploadWidth)
        const sendButtonHeight = RHButtons.compactHeight
        const inputRowHeight = [inputHeight, sendButtonHeight].max()
        const footerHeight = inputRowHeight + uploadHeight + RHSpacing.small * 3

        const headerFrame = bounds
            .rectangleWithHeight(headerHeight)
            .assignedAsFrameOfView(this.headerView)
        const activityWidth = Math.min(
            headerFrame.width * 0.45,
            this.lastActivityLabel.intrinsicContentWidth(headerHeight) + RHSpacing.small
        )
        headerFrame
            .rectangleWithInsets(RHSpacing.medium, RHSpacing.medium, 0, 0)
            .distributeViewsAlongWidth(
                [this.conversationTitleLabel, this.lastActivityLabel],
                undefined,
                RHSpacing.small,
                [nil, activityWidth]
            )

        const footerFrame = bounds.rectangleWithHeight(footerHeight, 1)
        const messageListHeight = [
            0,
            footerFrame.min.y - headerFrame.max.y
        ].max()
        this.messageListView.frame = headerFrame
            .rectangleForNextRow(0, messageListHeight)

        if (this.emptyStateLabel.superview === this) {
            this.emptyStateLabel.frame = this.messageListView.frame
        }

        this.dividerView.frame = footerFrame.rectangleWithHeight(1)

        footerFrame
            .rectangleWithInsets(
                RHSpacing.small,
                RHSpacing.small,
                RHSpacing.small,
                RHSpacing.small
            )
            .rectangleWithHeight(inputRowHeight)
            .distributeViewsAlongWidth(
                [this.inputArea, this.sendButton],
                undefined,
                RHSpacing.small,
                [nil, sendButtonWidth]
            )
            .rectangleForNextRow(RHSpacing.small, uploadHeight)
            .assignedAsFrameOfView(this.fileUploadView)

        this.inputArea.frame = this.inputArea.frame.rectangleWithHeight(inputHeight, 0.5)
        this.sendButton.frame = this.sendButton.frame.rectangleWithHeight(sendButtonHeight, 0.5)
    }

    override intrinsicContentHeight(constrainingWidth?: number) {
        return RHPrivateMessageThreadViewTokens.threadViewHeight
    }

}
